import React, { useEffect, useState } from "react"
import {
  Box,
  Typography,
  Grid,
  makeStyles,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core"
// @ts-ignore
import { useHistory } from "react-router-dom"
import useStore from "../utils/store"
import consts from "../utils/consts.json"
import CustomButton from "../components/form-components/custom-button"
import { TrashIcon2 } from "src/components/icons-svg"
import CustomCheckbox from "../components/form-components/custom-checkbox"
import { useAlertStore } from "../utils/alert-store"
import CustomSearch from "../components/form-components/custom-search"
import ReviewCard from "../components/app-components/review-card"

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
    backgroundColor: "#f5f5f5",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    overflow: "auto", // Allow scrolling only when needed
    position: "absolute", // Take it out of normal flow
    width: "100%", // Fill the entire width
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  headerSection: {
    position: "relative",
    marginBottom: theme.spacing(4),
    marginTop: "64px", // Add margin-top to account for app bar height (standard MUI app bar is 64px)
    padding: theme.spacing(4, 2, 4, 2),
    overflow: "hidden",
    backgroundImage: "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/background1.jpg)",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center",
    borderRadius: 0, // Remove border radius for full width
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flexShrink: 0, // Prevent header from shrinking
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2, 1, 2, 1),
      marginTop: "56px", // Mobile app bar height is typically 56px
    },
  },
  header: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    color: "#fff", // White text
    position: "relative",
    zIndex: 2,
    textAlign: "center",
  },
  subtitle: {
    color: "#fff", // White text
    opacity: 0.95,
    marginBottom: theme.spacing(3),
    position: "relative",
    zIndex: 2,
    textAlign: "center",
  },
  reviewListSection: {
    padding: theme.spacing(3),
    flexGrow: 1, // Allow this section to grow
    overflow: "auto", // Enable scrolling in this section if needed
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
  },
  searchContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2), // Match deleteSelectedBar's marginBottom
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  searchBox: {
    width: "100%",
    maxWidth: 400,
    background: "#fff",
    borderRadius: 8,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    [theme.breakpoints.down("xs")]: {
      maxWidth: "100%",
    },
  },
  deleteIconAlways: {
    color: theme.palette.error.main,
    background: "#fff",
    borderRadius: "50%",
    boxShadow: "0 2px 6px rgba(0,0,0,0.07)",
    "&:hover": {
      background: theme.palette.error.light,
      color: "#fff",
    },
  },
  deleteSelectedBar: {
    display: "flex",
    alignItems: "center",
    marginLeft: 0, // Align with left edge
    paddingLeft: 0, // Remove any default padding
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}))

export default function MyReviews() {
  const classes = useStyles()
  const history = useHistory()
  const fetchMyReviews = useStore((state) => state.fetchMyReviews)
  const deleteReviews = useStore((state) => state.deleteReviews)
  const myReviews = useStore((state) => state.myReviews)
  const [search, setSearch] = useState("")
  const [selected, setSelected] = useState([])
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleteMode, setDeleteMode] = useState("single") // "single" or "multi"
  const [deleteTarget, setDeleteTarget] = useState(null) // reviewId or array
  const showAlert = useAlertStore((state) => state.showAlert)

  useEffect(() => {
    fetchMyReviews()
  }, [])

  const filteredReviews = (myReviews || []).filter((review) =>
    review.name?.toLowerCase().includes(search.toLowerCase()),
  )

  // Select All logic
  const allSelected =
    filteredReviews.length > 0 &&
    filteredReviews.every((r) => selected.includes(r._id))
  const someSelected =
    filteredReviews.some((r) => selected.includes(r._id)) && !allSelected

  const handleSelect = (reviewId) => {
    setSelected((prev) =>
      prev.includes(reviewId)
        ? prev.filter((id) => id !== reviewId)
        : [...prev, reviewId],
    )
  }

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelected(filteredReviews.map((r) => r._id))
    } else {
      setSelected([])
    }
  }

  const handleDelete = (reviewId) => {
    setDeleteMode("single")
    setDeleteTarget([reviewId])
    setConfirmOpen(true)
  }

  const handleDeleteSelected = () => {
    setDeleteMode("multi")
    setDeleteTarget(selected)
    setConfirmOpen(true)
  }

  const handleConfirmDelete = async () => {
    await deleteReviews(deleteTarget, showAlert)
    setSelected((prev) => prev.filter((id) => !deleteTarget.includes(id)))
    setConfirmOpen(false)
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.headerSection}>
        <Typography variant="h4" className={classes.header}>
          My Reviews
        </Typography>
        <Typography variant="subtitle1" className={classes.subtitle}>
          Here you can find all your created reviews. Click on a review name to
          view more details.
        </Typography>
      </Box>
      <Box className={classes.reviewListSection}>
        <Box className={classes.searchContainer}>
          <CustomSearch
            placeholder="Search review name..."
            value={search}
            onChange={setSearch}
            className={classes.searchBox}
          />
          <CustomButton id={"create-new-review"} onClick={() => history.push(consts.ROUTES.LANDING)}>
            Create new review
          </CustomButton>
        </Box>
        <Box className={classes.deleteSelectedBar} style={{ paddingLeft: 0 }}>
          <CustomCheckbox
            checked={allSelected}
            indeterminate={someSelected}
            label="Select All"
            onChange={(checked) => handleSelectAll(checked)}
          />
          {selected.length > 0 && (
            <>
              <IconButton
                aria-label="delete-selected"
                className={classes.deleteIconAlways}
                onClick={handleDeleteSelected}
              >
                {TrashIcon2(20)}
              </IconButton>
              <Typography variant="body2">
                Delete Selected ({selected.length})
              </Typography>
            </>
          )}
        </Box>
        <Grid container spacing={3}>
          {filteredReviews.length === 0 && (
            <Grid item xs={12}>
              <Typography color="textSecondary">No reviews found.</Typography>
            </Grid>
          )}
          {filteredReviews.map((review, index) => (
            <Grid item xs={12} sm={6} md={4} key={review._id ? String(review._id) : `review-${index}`}>
              <ReviewCard
                review={review}
                isSelected={selected.includes(review._id)}
                onSelect={() => handleSelect(review._id)}
                onDelete={handleDelete}
              />
            </Grid>
          ))}
        </Grid>
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              {deleteMode === "single"
                ? "Are you sure you want to delete this review?"
                : `Are you sure you want to delete ${
                    deleteTarget?.length || 0
                  } selected reviews?`}
            </Typography>
          </DialogContent>
          <DialogActions>
            <CustomButton onClick={() => setConfirmOpen(false)} variant="link">
              Cancel
            </CustomButton>
            <CustomButton
              onClick={handleConfirmDelete}
              color="primary"
              variant="contained"
            >
              Delete
            </CustomButton>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  )
}
